import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';

import { refreshToken as refreshTokenRequest, revokeSession } from '@mygpt/app/apis';
import {
  KEY_MYGPT_TOKEN as GET_KEY_MYGPT_TOKEN,
  KEY_MYGPT_TOKEN_EXPIRESIN as GET_KEY_MYGPT_TOKEN_EXPIRESIN,
  KEY_MYGPT_REFRESH_TOKEN as GET_KEY_MYGPT_REFRESH_TOKEN,
  KEY_MYGPT_PATH_NAME,
  cookieConfig,
} from '@mygpt/app/apis/constant';
import { TokenInfo } from '@mygpt/types';

// Cookie Domain配置（使用共享的配置）
const getCookieDomain = () => cookieConfig.COOKIE_DOMAIN;

/**
 * 删除Token信息
 * 注意：必须动态调用 GET_KEY_MYGPT_TOKEN() 等函数获取 key，
 * 因为 key 依赖 stage（通过 initEnv 设置），模块加载时 stage 可能尚未初始化
 */
export const deleteTokenInfo = () => {
  const domain = getCookieDomain();
  Cookies.remove(GET_KEY_MYGPT_TOKEN(), { domain });
  Cookies.remove(GET_KEY_MYGPT_REFRESH_TOKEN(), { domain });
  Cookies.remove(GET_KEY_MYGPT_TOKEN_EXPIRESIN(), { domain });
};

/**
 * 保存Token信息
 */
export const saveTokenInfo = (tokenInfo: TokenInfo) => {
  const { access_token, refresh_token, expires_in, token_type } = tokenInfo;
  if (!access_token || !token_type || !expires_in) return;

  const domain = getCookieDomain();
  const now = new Date().getTime();
  const expiresTime = now + Number(expires_in) * 1000;

  Cookies.set(GET_KEY_MYGPT_TOKEN(), `${token_type} ${access_token}`, {
    domain,
    secure: location.protocol === 'https:',
    sameSite: 'lax',
  });
  Cookies.set(GET_KEY_MYGPT_TOKEN_EXPIRESIN(), String(expiresTime), {
    domain,
    secure: location.protocol === 'https:',
    sameSite: 'lax',
  });
  if (refresh_token) {
    Cookies.set(GET_KEY_MYGPT_REFRESH_TOKEN(), refresh_token, {
      domain,
      secure: location.protocol === 'https:',
      sameSite: 'lax',
    });
  }
};

/**
 * 获取本地登录页URL
 */
const getLocalLoginUrl = () => {
  return window.location.origin + '/auth/login';
};

/**
 * 获取API登录URL
 */
const getApiLoginUrl = () => {
  const apiBaseUrl = window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
  return apiBaseUrl + '/login';
};

export interface UseAuthOptions {
  /**
   * 是否为本地部署模式
   * 本地部署模式下，登录/登出直接跳转到本地页面
   * 线上模式下，调用后端OAuth接口
   */
  isLocalMode?: boolean;

  /**
   * 使用localStorage而非sessionStorage保存路径
   * admin使用localStorage（支持跨窗口），web使用sessionStorage
   */
  useLocalStorage?: boolean;
}

/**
 * 统一的认证Hook
 * 适用于admin、web、website的登录/登出逻辑
 */
export const useAuth = (options: UseAuthOptions = {}) => {
  const { isLocalMode = import.meta.env.VITE_MODE === 'local', useLocalStorage = false } = options;

  const location = useLocation();
  const navigate = useNavigate();

  /**
   * 保存当前路径（登录后返回）
   */
  const savePath = (path?: string) => {
    const pathName = path || location.pathname;
    const storage = useLocalStorage ? localStorage : sessionStorage;
    storage.setItem(KEY_MYGPT_PATH_NAME, pathName);
  };

  /**
   * 登录回调处理
   */
  const loginCallback = () => {
    const storage = useLocalStorage ? localStorage : sessionStorage;
    const pathname = storage.getItem(KEY_MYGPT_PATH_NAME);

    if (pathname && pathname !== location.pathname) {
      // 优先使用window.location.href确保完整刷新
      window.location.href = pathname;
    } else {
      navigate('/');
    }

    storage.removeItem(KEY_MYGPT_PATH_NAME);
  };

  /**
   * 登录
   */
  const login = (path?: string) => {
    savePath(path);

    if (isLocalMode) {
      // 本地部署模式：直接跳转到本地登录页
      console.log('[useAuth] Local mode - redirecting to /auth/login');
      window.location.href = '/auth/login';
    } else {
      // 线上模式：跳转到OAuth登录页
      window.location.href = '/auth/login';
    }
  };

  /**
   * 登出
   */
  const logout = async () => {
    await revokeSession(Cookies.get(GET_KEY_MYGPT_TOKEN())).catch(() => undefined);
    deleteTokenInfo();

    if (isLocalMode) {
      // 本地部署模式：直接跳转到本地登录页
      console.log('[useAuth] Local mode - redirecting to /auth/login');
      // 清理保存的路径
      if (useLocalStorage) {
        localStorage.removeItem(KEY_MYGPT_PATH_NAME);
      }
      window.location.href = '/auth/login';
    } else {
      // 线上模式：调用后端登出接口
      console.log('[useAuth] Online mode - calling backend logout API');
      // 清理保存的路径
      if (useLocalStorage) {
        localStorage.removeItem(KEY_MYGPT_PATH_NAME);
      }

      const apiBaseUrl = window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
      window.location.href = apiBaseUrl + `/logout?redirect_uri=${encodeURI(getLocalLoginUrl())}`;
    }
  };

  /**
   * 注册
   */
  const signup = (path?: string) => {
    savePath(path);

    const apiBaseUrl = window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
    window.location.href = apiBaseUrl + '/login?screen_hint=signup';
  };

  /**
   * 更新Token信息（公用方法）
   * 接受TokenInfo数据，保存并安排自动刷新
   * 可用于登录回调、接受邀请等任何返回新token的场景
   */
  const updateToken = (data: TokenInfo) => {
    saveTokenInfo(data);
    scheduleTokenRenewal();
  };

  /**
   * 刷新Token
   */
  const refreshToken = async () => {
    const refresh_token = Cookies.get(GET_KEY_MYGPT_REFRESH_TOKEN());
    if (!refresh_token) return;

    try {
      const res = await refreshTokenRequest({ refresh_token });
      if (!res.data) return;

      const data: TokenInfo = res.data;
      updateToken(data);
      return data;
    } catch (error) {
      console.error('[useAuth] Failed to refresh token:', error);
      // Token刷新失败，清除token并跳转登录
      logout();
    }
  };

  /**
   * 安排Token自动刷新
   */
  const scheduleTokenRenewal = () => {
    const expiresIn = Cookies.get(GET_KEY_MYGPT_TOKEN_EXPIRESIN());
    if (!expiresIn) return;

    const now = new Date().getTime();
    const delay = Number(expiresIn) - now - 60000; // 提前60秒刷新

    if (delay <= 0) {
      // Token已过期或即将过期，立即刷新
      refreshToken();
      return;
    }

    setTimeout(() => {
      refreshToken();
    }, delay);
  };

  /**
   * 获取登录URL（用于展示）
   */
  const getLoginUrl = () => {
    return getApiLoginUrl();
  };

  return {
    login,
    logout,
    signup,
    loginCallback,
    savePath,
    updateToken,
    refreshToken,
    scheduleTokenRenewal,
    getLoginUrl,
  };
};
